import { Model } from '../../infra/model'
import { OpportunityOffer as OpportunityOfferProto } from '@taxfyle/api-internal/internal/opportunity_pb'
import { Partial } from 'react-spring'
import { Id } from '../../types/definitions'
import { DateZero, maybeParseDate } from '../../utils/date-util'
import User from '../../iam/models/User'
import { computed, makeObservable, observable } from 'mobx'
import { PrimeUser } from '../../iam/UserStore'
import { decimalToNumber, durationToMinutes } from '../../utils/grpc-util'
import { timestampToISO } from '@taxfyle/backend-commons/lib/utils/grpc-util'
import { match } from '../../utils/pattern-match-util'
import { toStream, fromStream } from '../../utils/mobx-util'
import { switchMap } from 'rxjs'
import { countdown } from '../../utils/rx-util'
import { PrimeWorkspace } from '../../iam/WorkspaceStore'
import { GetCurrentMember } from '../../iam/SessionStore'
import Workspace from '../../iam/models/Workspace'
import { formatWithLegendPriceUnit } from '../../jobs/Job'
import { TimeRange } from '../../jobs/TimeRange'

/**
 * The action to be taken upon offer expiration.
 */
export enum OfferExpirationAction {
  /**
   * The opportunity will be automatically accepted.
   */
  Accept,

  /**
   * The opportunity will be automatically declined.
   */
  Decline,
}

/**
 * An opportunity offer.
 */
export class OpportunityOffer extends Model<
  OpportunityOfferProto.AsObject,
  OpportunityOffer
> {
  /**
   * An observable countdown. Ticks every second in sync with clock time until
   * the scheduled expiration time is reached.
   */
  private _timeUntilExpiration$ = toStream(() => this.dateExpires, true).pipe(
    switchMap(countdown),
  )

  /**
   * MobX-representation the time until expiration stream.
   */
  private _timeUntilExpiration = fromStream(null, this._timeUntilExpiration$)

  /**
   * The Opportunity ID.
   */
  id = Id.none

  /**
   * The workspace in which the opportunity is offered.
   */
  workspace = Workspace.none

  /**
   * The display name of the opportunity.
   */
  name = ''

  /**
   * The description for the opportunity.
   */
  description = ''

  /**
   * The legend name for the opportunity.
   */
  legendName = ''

  /**
   * The client for the opportunity.
   */
  client = OpportunityOfferClient.none

  /**
   * The job specs for the opportunity.
   */
  specs: string[] = []

  /**
   * The Provider Cut Amount for the opportunity.
   */
  providerCutAmount = 0

  /**
   * The date at which the opportunity was offered.
   */
  dateOffered = DateZero()

  /**
   * The date at which the opportunity expires.
   */
  dateExpires = DateZero()

  /**
   * The action to perform upon expiration.
   */
  expirationAction = OfferExpirationAction.Accept

  /**
   * Whether the Offer was targeted to the Provider by request.
   */
  targeted = false

  /**
   *  The consultation associated with the offer, if any.
   */
  consultation: OpportunityOfferConsultation | null = null

  constructor(
    private primeUser: PrimeUser,
    private primeWorkspace: PrimeWorkspace,
    private getCurrentMember: GetCurrentMember,
  ) {
    super()

    makeObservable(this, {
      id: observable,
      workspace: observable,
      name: observable,
      description: observable,
      legendName: observable,
      client: observable,
      specs: observable,
      providerCutAmount: observable,
      dateOffered: observable,
      dateExpires: observable,
      expirationAction: observable,
      targeted: observable,
      consultation: observable,
      displayName: computed,
      timeUntilExpiration: computed,
      shouldShowProviderCutAmount: computed,
      formattedProviderCutAmount: computed,
      currentMember: computed,
      expirationMillis: computed,
    })
  }

  /**
   * Get the display name of the offer
   */
  get displayName() {
    return this.name || this.description || this.legendName
  }

  /**
   * Countdown until expiration.
   */
  get timeUntilExpiration() {
    return this._timeUntilExpiration()
  }

  /**
   * Whether to show the earnings.
   */
  get shouldShowProviderCutAmount() {
    return this.providerCutAmount > 0
  }

  /**
   * Formatted provider cut amount.
   */
  get formattedProviderCutAmount() {
    if (!this.shouldShowProviderCutAmount) {
      return ''
    }

    return formatWithLegendPriceUnit(undefined, this.providerCutAmount)
  }

  /**
   * The current member.
   */
  get currentMember() {
    return this.getCurrentMember(this.workspace.id)
  }

  /**
   * The date at which the offer expires, in milliseconds.
   */
  get expirationMillis() {
    return this.dateExpires.getTime()
  }

  /**
   * @inheritDoc
   */
  parse(
    attrs: Partial<OpportunityOfferProto.AsObject>,
  ): Partial<OpportunityOffer> {
    return {
      id: attrs.id,
      workspace: this.primeWorkspace({ id: attrs.workspaceId }),
      name: attrs.name,
      description: attrs.description,
      legendName: attrs.legendName,
      client: attrs.client
        ? new OpportunityOfferClient(
            this.primeUser({
              public_id: attrs.client.userPublicId,
              given_name: attrs.client.givenName?.value,
              family_name: attrs.client.familyName?.value,
              picture: attrs.client.picture?.value,
            }),
          )
        : undefined,
      specs: attrs.specsList,
      providerCutAmount: attrs.providerCutAmount
        ? decimalToNumber(attrs.providerCutAmount)
        : undefined,
      dateOffered:
        maybeParseDate(timestampToISO(attrs.dateOffered)) ?? this.dateOffered,
      dateExpires:
        maybeParseDate(timestampToISO(attrs.dateExpires)) ?? this.dateExpires,
      expirationAction: attrs.expirationAction
        ? mapExpirationAction(attrs.expirationAction)
        : this.expirationAction,
      targeted: attrs.targeted,
      consultation: attrs.consultation
        ? new OpportunityOfferConsultation(
            attrs.consultation.id,
            attrs.consultation.availabilityTimesList.map(
              TimeRange.fromInterval,
            ),
            attrs.consultation.requestedLength
              ? durationToMinutes(attrs.consultation.requestedLength)
              : 30,
          )
        : attrs.consultation,
    }
  }
}

/**
 * The offer client.
 */
class OpportunityOfferClient {
  constructor(private _user: User) {
    makeObservable(this, {
      displayName: computed,
      user: computed,
    })
  }

  /**
   * Empty instance.
   */
  static readonly none = new OpportunityOfferClient(User.none)

  /**
   * The client's display name.
   */
  get displayName() {
    return this._user.displayName
  }

  /**
   * The user.
   */
  get user() {
    return this._user
  }
}

/**
 * A Opportunity Offer-representation of a Consultation.
 */
class OpportunityOfferConsultation {
  /**
   * The ID of the Consultation.
   */
  consultationId = Id.none

  /**
   * The availability times for the Consultation.
   */
  availabilityTimes: TimeRange[] = []

  /**
   * The requested length for the Consultation.
   */
  requestedLength: number = 0

  constructor(
    consultationId: Id,
    availabilityTimes: TimeRange[],
    requestedLength: number,
  ) {
    makeObservable(this, {
      consultationId: observable,
      availabilityTimes: observable,
      requestedLength: observable,
    })

    this.consultationId = consultationId
    this.availabilityTimes = availabilityTimes.flatMap((a) =>
      a.decomposeRangeInIntervals(requestedLength),
    )
    this.requestedLength = requestedLength
  }

  /**
   * Empty instance.
   */
  static readonly none = new OpportunityOfferConsultation(Id.none, [], 0)
}

/**
 * Maps the expiration action from a proto to a domain representation.
 */
function mapExpirationAction(action: OpportunityOfferProto.ExpirationAction) {
  return match(action, {
    [OpportunityOfferProto.ExpirationAction.ACCEPT]: () =>
      OfferExpirationAction.Accept,
    [OpportunityOfferProto.ExpirationAction.DECLINE]: () =>
      OfferExpirationAction.Decline,
    [OpportunityOfferProto.ExpirationAction.UNSPECIFIED]: () => {
      throw new Error('Unreachable')
    },
  })
}
